import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import {
  Video,
  Save,
  Link as LinkIcon,
  Radio,
  ToggleLeft,
  ToggleRight,
  Truck,
  ShieldCheck,
  CheckCircle2,
  AlertCircle,
  Activity,
  Key,
  Compass,
  ArrowUp,
  ArrowDown,
  Plus,
  Trash2
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_MENU_ITEMS = [
  { id: 'store', name: 'Divine Store', href: '/store', icon: 'Store', is_active: true, order: 1 },
  { id: 'pujas', name: 'Online Pujas', href: '/pujas', icon: 'Sparkles', is_active: true, order: 2 },
  { id: 'offerings', name: 'Offerings', href: '/offerings', icon: 'Flame', is_active: true, order: 3 },
  { id: 'pilgrimage', name: 'Pilgrimage', href: '/travel', icon: 'MapPin', is_active: true, order: 4 },
];

const AdminSettings = () => {
  const [heroSettings, setHeroSettings] = useState({ video_url: '', headline: '', subheadline: '' });
  const [liveSession, setLiveSession] = useState({ is_active: false, stream_url: '', display_text: '', temple_name: '' });
  const [menuItems, setMenuItems] = useState(DEFAULT_MENU_ITEMS);
  const [savingMenu, setSavingMenu] = useState(false);
  
  // Shiprocket settings
  const [srSettings, setSrSettings] = useState({
    email: '',
    password: '',
    pickup_pincode: '249401',
    pickup_location_name: 'Haridwar Central Hub',
    auto_ship: false,
    is_configured: false,
    has_password: false
  });

  const [loading, setLoading] = useState(true);
  const [savingHero, setSavingHero] = useState(false);
  const [savingLive, setSavingLive] = useState(false);
  const [savingSr, setSavingSr] = useState(false);
  const [testingSr, setTestingSr] = useState(false);
  const [srTestResult, setSrTestResult] = useState(null);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const [heroRes, liveRes, srRes, menuRes] = await Promise.all([
        axios.get(`${API}/settings/hero`).catch(() => ({ data: {} })),
        axios.get(`${API}/settings/live`).catch(() => ({ data: {} })),
        axios.get(`${API}/shipping/shiprocket/settings`, { withCredentials: true }).catch(() => ({ data: {} })),
        axios.get(`${API}/settings/menu`).catch(() => ({ data: {} }))
      ]);

      if (menuRes.data?.menu_items && Array.isArray(menuRes.data.menu_items)) {
        setMenuItems(menuRes.data.menu_items);
      }

      setHeroSettings({
        video_url: heroRes.data?.video_url || '',
        headline: heroRes.data?.headline || '',
        subheadline: heroRes.data?.subheadline || ''
      });

      setLiveSession({
        is_active: liveRes.data?.is_active || false,
        stream_url: liveRes.data?.stream_url || '',
        display_text: liveRes.data?.display_text || '',
        temple_name: liveRes.data?.temple_name || ''
      });

      if (srRes.data) {
        setSrSettings({
          email: srRes.data.email || '',
          password: '',
          pickup_pincode: srRes.data.pickup_pincode || '249401',
          pickup_location_name: srRes.data.pickup_location_name || 'Haridwar Central Hub',
          auto_ship: !!srRes.data.auto_ship,
          is_configured: !!srRes.data.is_configured,
          has_password: !!srRes.data.has_password
        });
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveHero = async () => {
    setSavingHero(true);
    try {
      await axios.put(`${API}/settings/hero`, heroSettings, { withCredentials: true });
      toast.success('Hero settings updated');
    } catch (error) {
      toast.error('Failed to update hero settings');
    } finally {
      setSavingHero(false);
    }
  };

  const handleSaveLive = async () => {
    setSavingLive(true);
    try {
      await axios.put(`${API}/settings/live`, liveSession, { withCredentials: true });
      toast.success('Live session updated');
    } catch (error) {
      toast.error('Failed to update live session');
    } finally {
      setSavingLive(false);
    }
  };

  const handleSaveShiprocket = async () => {
    setSavingSr(true);
    try {
      const payload = {
        email: srSettings.email,
        pickup_pincode: srSettings.pickup_pincode,
        pickup_location_name: srSettings.pickup_location_name,
        auto_ship: srSettings.auto_ship
      };
      if (srSettings.password) {
        payload.password = srSettings.password;
      }

      await axios.put(`${API}/shipping/shiprocket/settings`, payload, { withCredentials: true });
      toast.success('Shiprocket settings saved successfully!');
      fetchSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save Shiprocket settings');
    } finally {
      setSavingSr(false);
    }
  };

  const handleTestShiprocket = async () => {
    setTestingSr(true);
    setSrTestResult(null);
    try {
      const res = await axios.post(`${API}/shipping/shiprocket/test-auth`, {
        email: srSettings.email,
        password: srSettings.password
      }, { withCredentials: true });

      setSrTestResult(res.data);
      if (res.data.success) {
        toast.success(res.data.message || 'Connected to Shiprocket successfully!');
      } else {
        toast.error(res.data.message || 'Shiprocket authentication failed');
      }
    } catch (error) {
      setSrTestResult({ success: false, message: error.response?.data?.message || 'Connection failed' });
      toast.error('Shiprocket connection test failed');
    } finally {
      setTestingSr(false);
    }
  };

  const handleUpdateMenuItem = (index, field, value) => {
    const updated = [...menuItems];
    updated[index][field] = value;
    setMenuItems(updated);
  };

  const handleMoveMenu = (index, direction) => {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= menuItems.length) return;
    const updated = [...menuItems];
    const temp = updated[index];
    updated[index] = updated[targetIndex];
    updated[targetIndex] = temp;
    updated.forEach((item, idx) => {
      item.order = idx + 1;
    });
    setMenuItems(updated);
  };

  const handleAddMenuItem = () => {
    const newItem = {
      id: `menu_${Date.now()}`,
      name: 'New Item',
      href: '/store',
      icon: 'Sparkles',
      is_active: true,
      order: menuItems.length + 1
    };
    setMenuItems([...menuItems, newItem]);
  };

  const handleDeleteMenuItem = (id) => {
    if (menuItems.length <= 1) {
      toast.error('At least one menu item is required');
      return;
    }
    setMenuItems(menuItems.filter(item => item.id !== id));
  };

  const handleSaveMenu = async () => {
    setSavingMenu(true);
    try {
      await axios.put(`${API}/settings/menu`, { menu_items: menuItems }, { withCredentials: true });
      toast.success('Navigation menu saved successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save navigation menu');
    } finally {
      setSavingMenu(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="spinner"></div></div>;

  return (
    <div className="space-y-6">
      
      {/* ═══ Navigation Menu Manager (Item 1 & 2) ═══ */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl p-6 shadow-sm border border-gray-100"
      >
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#FF7B00]/10 flex items-center justify-center">
              <Compass className="w-5 h-5 text-[#FF7B00]" />
            </div>
            <div>
              <h3 className="font-heading text-lg font-bold text-[#2D2D2D]">Header Navigation Menu Manager</h3>
              <p className="text-sm text-[#718096]">
                Configure main site menu items (e.g. Divine Store, Online Pujas, Offerings, Pilgrimage)
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleAddMenuItem}
              className="text-xs font-semibold"
            >
              <Plus className="w-3.5 h-3.5 mr-1" />
              Add Menu Item
            </Button>
            <Button
              onClick={handleSaveMenu}
              disabled={savingMenu}
              className="btn-saffron text-xs font-bold"
              data-testid="save-menu-settings-btn"
            >
              <Save className="w-3.5 h-3.5 mr-1.5" />
              {savingMenu ? 'Saving...' : 'Save Navigation Menu'}
            </Button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="bg-gray-50 border-b border-gray-100 text-xs font-bold text-gray-500 uppercase">
              <tr>
                <th className="py-2.5 px-3 w-16 text-center">Order</th>
                <th className="py-2.5 px-3">Menu Label</th>
                <th className="py-2.5 px-3">Destination URL / Route</th>
                <th className="py-2.5 px-3 w-28 text-center">Visibility</th>
                <th className="py-2.5 px-3 w-16 text-center">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {menuItems.map((item, index) => (
                <tr key={item.id || index} className="hover:bg-gray-50/50">
                  <td className="py-2 px-3 text-center">
                    <div className="flex items-center justify-center gap-1">
                      <button
                        type="button"
                        disabled={index === 0}
                        onClick={() => handleMoveMenu(index, -1)}
                        className="p-1 hover:bg-gray-200 disabled:opacity-30 rounded transition-colors"
                        title="Move Up"
                      >
                        <ArrowUp className="w-3.5 h-3.5" />
                      </button>
                      <button
                        type="button"
                        disabled={index === menuItems.length - 1}
                        onClick={() => handleMoveMenu(index, 1)}
                        className="p-1 hover:bg-gray-200 disabled:opacity-30 rounded transition-colors"
                        title="Move Down"
                      >
                        <ArrowDown className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                  <td className="py-2 px-3">
                    <Input
                      value={item.name}
                      onChange={(e) => handleUpdateMenuItem(index, 'name', e.target.value)}
                      placeholder="e.g. Online Pujas"
                      className="h-9 text-xs sm:text-sm font-semibold text-[#2A1F16]"
                    />
                  </td>
                  <td className="py-2 px-3">
                    <Input
                      value={item.href}
                      onChange={(e) => handleUpdateMenuItem(index, 'href', e.target.value)}
                      placeholder="e.g. /pujas"
                      className="h-9 text-xs sm:text-sm font-mono text-gray-600"
                    />
                  </td>
                  <td className="py-2 px-3 text-center">
                    <button
                      type="button"
                      onClick={() => handleUpdateMenuItem(index, 'is_active', !item.is_active)}
                      className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold transition-all ${
                        item.is_active !== false
                          ? 'bg-emerald-100 text-emerald-800 border border-emerald-200'
                          : 'bg-gray-100 text-gray-500 border border-gray-200'
                      }`}
                    >
                      {item.is_active !== false ? 'Visible' : 'Hidden'}
                    </button>
                  </td>
                  <td className="py-2 px-3 text-center">
                    <button
                      type="button"
                      onClick={() => handleDeleteMenuItem(item.id)}
                      className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Remove Menu Item"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </motion.div>

      {/* Shiprocket Logistics Manager */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl p-6 shadow-sm border border-gray-100"
      >
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#6E1B34] text-white flex items-center justify-center">
              <Truck className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-heading text-lg font-bold text-[#2D2D2D]">Shiprocket Logistics Integration</h3>
                {srSettings.is_configured ? (
                  <span className="inline-flex items-center gap-1 text-[11px] font-bold bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-full border border-emerald-200">
                    <CheckCircle2 className="w-3 h-3" />
                    Connected
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-[11px] font-bold bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full border border-amber-200">
                    <AlertCircle className="w-3 h-3" />
                    Demo/Simulation Mode Active
                  </span>
                )}
              </div>
              <p className="text-sm text-[#718096]">Configure Shiprocket API credentials for automatic courier assignment and devotee package selection</p>
            </div>
          </div>

          <Button
            type="button"
            variant="outline"
            onClick={handleTestShiprocket}
            disabled={testingSr || (!srSettings.email && !srSettings.is_configured)}
            className="border-gray-300 text-xs font-semibold"
          >
            <Activity className="w-3.5 h-3.5 mr-1.5 text-[#D9711F]" />
            {testingSr ? 'Testing...' : 'Test Connection'}
          </Button>
        </div>

        {srTestResult && (
          <div className={`p-3.5 rounded-xl mb-4 text-xs flex items-center justify-between border ${
            srTestResult.success ? 'bg-emerald-50 text-emerald-800 border-emerald-200' : 'bg-red-50 text-red-700 border-red-200'
          }`}>
            <div className="flex items-center gap-2">
              {srTestResult.success ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
              <span>{srTestResult.message}</span>
            </div>
            <button onClick={() => setSrTestResult(null)} className="text-xs underline font-bold">Dismiss</button>
          </div>
        )}

        <div className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-bold text-gray-700">Shiprocket API Email</Label>
              <Input
                type="email"
                value={srSettings.email}
                onChange={(e) => setSrSettings({ ...srSettings, email: e.target.value })}
                placeholder="your-shiprocket-account@domain.com"
                className="mt-1 text-xs sm:text-sm"
              />
            </div>

            <div>
              <Label className="text-xs font-bold text-gray-700">
                Shiprocket Account Password {srSettings.has_password && <span className="text-emerald-600 font-normal">(Password configured)</span>}
              </Label>
              <Input
                type="password"
                value={srSettings.password}
                onChange={(e) => setSrSettings({ ...srSettings, password: e.target.value })}
                placeholder={srSettings.has_password ? '•••••••••••• (Leave blank to keep current)' : 'Enter API password'}
                className="mt-1 text-xs sm:text-sm"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-bold text-gray-700">Origin / Central Hub Pickup PIN Code</Label>
              <Input
                value={srSettings.pickup_pincode}
                onChange={(e) => setSrSettings({ ...srSettings, pickup_pincode: e.target.value })}
                placeholder="249401"
                className="mt-1 text-xs sm:text-sm font-mono font-bold"
              />
              <p className="text-[11px] text-gray-400 mt-0.5">Primary consecrated dispatch facility (e.g. Haridwar)</p>
            </div>

            <div>
              <Label className="text-xs font-bold text-gray-700">Pickup Location / Warehouse Nickname</Label>
              <Input
                value={srSettings.pickup_location_name}
                onChange={(e) => setSrSettings({ ...srSettings, pickup_location_name: e.target.value })}
                placeholder="Haridwar Central Hub"
                className="mt-1 text-xs sm:text-sm"
              />
              <p className="text-[11px] text-gray-400 mt-0.5">Matches the warehouse nickname created in your Shiprocket panel</p>
            </div>
          </div>

          {/* Auto-Ship Toggle */}
          <div className="pt-2">
            <label className="flex items-start gap-3 p-3 bg-gray-50 border border-gray-200 rounded-xl cursor-pointer hover:bg-gray-100/70 transition-colors">
              <input
                type="checkbox"
                checked={srSettings.auto_ship}
                onChange={(e) => setSrSettings({ ...srSettings, auto_ship: e.target.checked })}
                className="mt-0.5 h-4 w-4 text-[#6E1B34] rounded border-gray-300 focus:ring-[#6E1B34]"
              />
              <div>
                <span className="font-bold text-[#2A1F16] text-xs">
                  Automatically Push Orders to Shiprocket on Payment Confirmation
                </span>
                <p className="text-[11px] text-[#718096]">
                  When enabled, verified Razorpay payments and confirmed COD orders are automatically initialized in your Shiprocket account with the devotee's chosen shipping package.
                </p>
              </div>
            </label>
          </div>

          <div className="flex justify-end pt-2">
            <Button
              onClick={handleSaveShiprocket}
              disabled={savingSr}
              className="bg-[#6E1B34] hover:bg-[#4E1225] text-white font-bold text-xs"
            >
              <Save className="w-4 h-4 mr-1.5" />
              {savingSr ? 'Saving...' : 'Save Shiprocket Settings'}
            </Button>
          </div>
        </div>
      </motion.div>

      {/* Live Session Manager */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl p-6 shadow-sm border border-gray-100"
      >
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center">
              <Radio className="w-5 h-5 text-red-500" />
            </div>
            <div>
              <h3 className="font-heading text-lg font-bold text-[#2D2D2D]">Live Session Manager</h3>
              <p className="text-sm text-[#718096]">Control the "Live Now" badge on the homepage</p>
            </div>
          </div>
          {/* Go Live Toggle */}
          <button
            onClick={() => setLiveSession(prev => ({ ...prev, is_active: !prev.is_active }))}
            className={`flex items-center gap-2 px-4 py-2 rounded-full font-semibold text-sm transition-all ${
              liveSession.is_active 
                ? 'bg-red-500 text-white shadow-[0_0_20px_rgba(239,68,68,0.3)]' 
                : 'bg-gray-100 text-gray-500'
            }`}
            data-testid="go-live-toggle"
          >
            {liveSession.is_active ? <ToggleRight className="w-5 h-5" /> : <ToggleLeft className="w-5 h-5" />}
            {liveSession.is_active ? 'LIVE' : 'Offline'}
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <Label>Stream Link (YouTube/Zoom URL)</Label>
            <Input
              value={liveSession.stream_url}
              onChange={(e) => setLiveSession({ ...liveSession, stream_url: e.target.value })}
              placeholder="https://youtube.com/live/..."
              data-testid="live-stream-url-input"
            />
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label>Display Text</Label>
              <Input
                value={liveSession.display_text}
                onChange={(e) => setLiveSession({ ...liveSession, display_text: e.target.value })}
                placeholder="Join Kedarnath Evening Aarti"
                data-testid="live-display-text-input"
              />
            </div>
            <div>
              <Label>Temple Name</Label>
              <Input
                value={liveSession.temple_name}
                onChange={(e) => setLiveSession({ ...liveSession, temple_name: e.target.value })}
                placeholder="Kedarnath"
                data-testid="live-temple-name-input"
              />
            </div>
          </div>
          <div className="flex justify-end">
            <Button onClick={handleSaveLive} disabled={savingLive} className="btn-saffron" data-testid="save-live-btn">
              <Save className="w-4 h-4 mr-2" />
              {savingLive ? 'Saving...' : 'Save Live Settings'}
            </Button>
          </div>
        </div>
      </motion.div>

      {/* Hero Video Settings */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white rounded-xl p-6 shadow-sm border border-gray-100"
      >
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-[#FF9933]/10 flex items-center justify-center">
            <Video className="w-5 h-5 text-[#FF9933]" />
          </div>
          <div>
            <h3 className="font-heading text-lg font-bold text-[#2D2D2D]">Hero Section Settings</h3>
            <p className="text-sm text-[#718096]">Customize the homepage hero video and text</p>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <Label>Background Video URL</Label>
            <p className="text-xs text-gray-400 mb-1">Direct link to an MP4 video file</p>
            <div className="flex gap-2">
              <Input
                value={heroSettings.video_url}
                onChange={(e) => setHeroSettings({ ...heroSettings, video_url: e.target.value })}
                placeholder="https://..."
                data-testid="hero-video-url-input"
                className="flex-1"
              />
              <a href="https://www.pexels.com/search/videos/temple/" target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-1 px-3 py-2 text-xs text-[#FF9933] border border-[#FF9933]/20 rounded-lg hover:bg-[#FF9933]/5">
                <LinkIcon className="w-3 h-3" /> Browse
              </a>
            </div>
          </div>
          {heroSettings.video_url && (
            <div className="rounded-xl overflow-hidden bg-black aspect-video max-w-lg">
              <video src={heroSettings.video_url} className="w-full h-full object-cover" autoPlay muted loop playsInline />
            </div>
          )}
          <div>
            <Label>Main Headline</Label>
            <Input value={heroSettings.headline} onChange={(e) => setHeroSettings({ ...heroSettings, headline: e.target.value })} data-testid="hero-headline-input" />
          </div>
          <div>
            <Label>Subheadline</Label>
            <Input value={heroSettings.subheadline} onChange={(e) => setHeroSettings({ ...heroSettings, subheadline: e.target.value })} data-testid="hero-subheadline-input" />
          </div>
          <div className="flex justify-end">
            <Button onClick={handleSaveHero} disabled={savingHero} className="btn-saffron" data-testid="save-hero-settings-btn">
              <Save className="w-4 h-4 mr-2" />
              {savingHero ? 'Saving...' : 'Save Hero Settings'}
            </Button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default AdminSettings;
